import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { DirectSubscriptionService } from '@app/modules/direct/services/direct-subscription.service';
import { NotificationType } from '@app/shared/interfaces';
import { MarkupsService, PlatformMarkup } from '@app/features/pricing-and-availability';
import { OptimizelyService } from '@app/shared/services/optimizely/optimizely.service';
import { SettingsService } from '@app/shared/services/settings/settings.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { take } from 'rxjs/operators';
import { PropertySettings } from '../../models/property-settings';

@Component({
  standalone: false,
  selector: 'sbnb-settings-properties',
  templateUrl: './settings-properties.component.html',
  styleUrls: ['./settings-properties.component.scss'],
})
export class SettingsPropertiesComponent implements OnInit {
  propertiesData: PropertySettings;
  resetting: boolean;
  saveStatus: number;
  searchCriteria: string;

  markupsData: PlatformMarkup[];

  airbnbMarkup: number;
  vrboMarkup: number;
  bookingMarkup: number;
  agodaMarkup: number;
  directMarkup: number;
  savingMarkups: boolean;

  selectedLeadPlatform: 'airbnb' | 'homeaway' | 'booking' | null = null;

  @ViewChild('bookingInput') bookingInput: ElementRef;
  @ViewChild('agodaInput') agodaInput: ElementRef;
  @ViewChild('homeawayInput') homeawayInput: ElementRef;
  @ViewChild('airbnbInput') airbnbInput: ElementRef;
  @ViewChild('directInput') directInput: ElementRef;
  savingLeadPlatform: boolean;

  directEnabled$ = this.directSubscription.isOnboarded$;

  constructor(
    private toast: ToastNotificationsService,
    private settingsService: SettingsService,
    private markupsService: MarkupsService,
    private directSubscription: DirectSubscriptionService,
    private optimizely: OptimizelyService
  ) {}

  ngOnInit() {
    this.settingsService.getPropertySettings().subscribe((res) => {
      this.propertiesData = res;
      this.selectedLeadPlatform = res.lead_platform;
    });

    this.markupsService.getPlatformMarkups().subscribe((res) => {
      this.markupsData = res;
      this.airbnbMarkup = this.markupsData.find((x) => x.platform === 'airbnb').markup;
      this.vrboMarkup = this.markupsData.find((x) => x.platform === 'homeaway').markup;
      this.bookingMarkup = this.markupsData.find((x) => x.platform === 'booking').markup;
      this.agodaMarkup = this.markupsData.find((x) => x.platform === 'agoda').markup;
      this.directMarkup = this.markupsData.find((x) => x.platform === 'direct').markup;
    });
  }

  saveLeadPlatform(platform: string) {
    this.savingLeadPlatform = true;
    this.saveStatus = 1;

    const payload = {
      lead_platform: platform,
    };

    this.settingsService.updatePropertySettings(payload).subscribe((res) => {
      this.saveStatus = 2;
      this.savingLeadPlatform = false;

      setTimeout(() => {
        this.saveStatus = 0;
      }, 2000);
    });
  }

  saveMarkups() {
    this.savingMarkups = true;

    const payload = [
      {
        platform: 'booking',
        markup: this.bookingInput.nativeElement.value / 100,
      },
      {
        platform: 'airbnb',
        markup: this.airbnbInput.nativeElement.value / 100,
      },
      {
        platform: 'homeaway',
        markup: this.homeawayInput.nativeElement.value / 100,
      },
      {
        platform: 'agoda',
        markup: this.agodaInput.nativeElement.value / 100,
      },
    ];

    // Add Direct markup only if the input exists
    if (this.directInput && this.directInput.nativeElement) {
      payload.push({
        platform: 'direct',
        markup: this.directInput.nativeElement.value / 100,
      });
    }

    this.saveStatus = 1;

    this.markupsService.updatePlatformMarkups(payload).subscribe((res) => {
      this.saveStatus = 2;

      setTimeout(() => {
        this.saveStatus = 0;
        this.savingMarkups = false;
      }, 2000);
    });
  }

  resetPropertyNamesClicked() {
    this.resetting = true;
    this.settingsService
      .resetPropertyNames()
      .pipe(take(1))
      .subscribe((res) => {
        this.resetting = false;
        let message = '';

        if (res.data) {
          message = 'Property names have been reset successfully';
        } else {
          message = 'There was an error resetting property names';
        }

        this.toast.open(message, 'Dismiss', NotificationType.Error);
      });
  }
}
